import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FadeIn from "@/components/FadeIn";
import VideoCarousel from "@/components/VideoCarousel";
import { CheckCircle2, ArrowRight, Image, Users, ShoppingBag, CreditCard, Mail, Upload, Download, CheckCircle, Star } from "lucide-react";

const Index = () => {
  const { toast } = useToast();

  const features = [
    {
      icon: <ShoppingBag className="w-5 h-5" />,
      title: "Customer Purchase",
      description: "Customer buys your product on your Shopify store"
    }, {
      icon: <Mail className="w-5 h-5" />,
      title: "Email Delivery",
      description: "After delivery, they receive an email with an upload link"
    }, {
      icon: <Upload className="w-5 h-5" />,
      title: "Photo Upload",
      description: "Customers upload authentic photos with your product"
    }, {
      icon: <CheckCircle className="w-5 h-5" />,
      title: "Brand Review",
      description: "You review and approve the best customer photos"
    }, {
      icon: <Download className="w-5 h-5" />,
      title: "Content Access",
      description: "Download approved photos for your marketing materials"
    }
  ];

  const benefits = [
    {
      title: "Authentic Content",
      description: "Build trust with real customer photos instead of staged product shots"
    }, {
      title: "Higher Conversion",
      description: "User-generated content can increase conversion rates by up to 161%"
    }, {
      title: "Social Proof",
      description: "Leverage customer photos as powerful social proof in your marketing"
    }, {
      title: "Save on Production",
      description: "Reduce the need for expensive product photography shoots"
    }
  ];

  const videoUrls = [
    "https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268211a9c869a6af5cca7_Lumineux%20Gif1-15-transcode.mp4",
    "https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268a8dbac3e65387d4a5e_Kane%20Gif2-14-transcode.mp4",
    "https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268ad10743628cc81baef_Kane%20Gif1-13-transcode.mp4",
    "https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268b3218cf85f1c4770e6_Huppy%20Gif2-12-transcode.mp4"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />

      <main className="container mx-auto px-4 py-12">
        <section className="pt-24 md:pt-32 pb-24 relative mx-0">
          <div className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10 -z-10" style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1542038784456-1ea8e935640e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')"
          }} />

          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent -z-10" />
          <div className="absolute top-0 inset-x-0 h-[500px] -z-10" style={{
            background: "radial-gradient(50% 50% at 50% 50%, rgba(24, 118, 255, 0.1) 0%, rgba(24, 118, 255, 0) 100%)"
          }} />

          <div className="container mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-16">
              <div className="w-full md:w-7/12 text-center md:text-left order-1">
                <FadeIn className="space-y-4">
                  <h1 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight md:leading-tight">
                    Real Customers. <span className="text-gradient">Reel Content. </span> Real Rewards.
                  </h1>

                  <p className="text-lg md:text-xl text-muted-foreground max-w-2xl md:mx-0 mx-auto">
                    The easiest way to gather and manage user-generated content for your brand. Turn happy customers into your most powerful marketing asset.
                  </p>

                  <div className="pt-8 flex flex-col sm:flex-row gap-4 sm:justify-start justify-center">
                    <Button size="lg" asChild>
                      <Link to="/register">
                        Get Started <ArrowRight className="ml-2 h-4 w-4" />
                      </Link>
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      {/* <Link to="/upload">Try Upload Demo</Link> */}
                    </Button>
                  </div>
                </FadeIn>
              </div>

              <div className="w-full md:w-5/12 order-2 mt-8 md:mt-0">
                <FadeIn delay={300} className="grid grid-cols-2 gap-3 max-w-[400px] mx-auto">
                  <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-md">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src={videoUrls[0]} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-md mt-6">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src={videoUrls[1]} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-md">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src={videoUrls[2]} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  <div className="aspect-[9/16] rounded-lg overflow-hidden shadow-md mt-6">
                    <video
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    >
                      <source src={videoUrls[3]} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </FadeIn>
              </div>
            </div>

            <section className="py-24 relative overflow-hidden">
              <div className="container max-w-6xl">
                <FadeIn className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">How It Works</h2>
                  <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                    A seamless workflow to collect, review and utilize authentic customer photos for your brand
                  </p>
                </FadeIn>

                <div className="relative mt-20">
                  <div className="absolute top-1/2 left-16 right-16 h-0.5 bg-muted -translate-y-1/2 hidden md:block" />

                  <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
                    {features.map((feature, index) => <FadeIn key={index} delay={index * 100} className="relative">
                      <div className="flex flex-col items-center text-center">
                        <div className="z-10 flex items-center justify-center w-12 h-12 rounded-full bg-primary text-white mb-8 shadow-lg relative">
                          {feature.icon}
                          <span className="absolute -top-10 text-sm font-semibold text-muted-foreground">
                            Step {index + 1}
                          </span>
                        </div>
                        <h3 className="text-lg font-medium mb-2">{feature.title}</h3>
                        <p className="text-muted-foreground text-sm">{feature.description}</p>
                      </div>
                    </FadeIn>)}
                  </div>
                </div>
              </div>
            </section>

            <section className="py-24 bg-background">
              <div className="container max-w-6xl">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                  <FadeIn className="space-y-6">
                    <h2 className="text-3xl md:text-4xl font-bold">See ShopSnap in Action</h2>
                    <p className="text-muted-foreground text-lg">
                      Watch how easily brands collect, manage, and utilize authentic customer photos to enhance their marketing and increase conversions.
                    </p>

                    <div className="space-y-4 pt-4">
                      {[
                        "Seamless customer photo collection",
                        "Simple approval workflow",
                        "Easy integration with your marketing",
                        "Built-in customer incentives"
                      ].map((feature, index) => (
                        <FadeIn key={index} delay={index * 100} className="flex gap-4">
                          <div className="flex-shrink-0 flex items-start pt-1">
                            <div className="rounded-full bg-primary/10 p-1">
                              <CheckCircle2 className="h-5 w-5 text-primary" />
                            </div>
                          </div>
                          <div>
                            <p className="text-foreground">{feature}</p>
                          </div>
                        </FadeIn>
                      ))}
                    </div>

                    <div className="pt-6">
                      <Button size="lg" asChild>
                        <Link to="/register">
                          Start Your Free Trial <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  </FadeIn>

                  <FadeIn direction="left">
                    <div className="rounded-lg overflow-hidden shadow-xl border border-muted">
                      <div className="aspect-video w-full">
                        <video
                          className="w-full h-full object-cover"
                          autoPlay
                          loop
                          muted
                          playsInline
                        >
                          <source src="https://billo.app/wp-content/uploads/2023/10/HERO.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  </FadeIn>
                </div>
              </div>
            </section>

            <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8">
              {[{
                icon: <Image className="w-5 h-5" />,
                number: "1,000+",
                label: "Photos Collected"
              }, {
                icon: <Users className="w-5 h-5" />,
                number: "500+",
                label: "Brands"
              }, {
                icon: <Star className="w-5 h-5" />,
                number: "98%",
                label: "Satisfaction"
              }].map((stat, index) => <FadeIn key={index} delay={index * 100} className="flex flex-col items-center p-6 rounded-xl">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-4">
                  {stat.icon}
                </div>
                <h3 className="text-3xl font-bold">{stat.number}</h3>
                <p className="text-muted-foreground">{stat.label}</p>
              </FadeIn>)}
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/50">
          <div className="container max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <FadeIn className="space-y-6">
                <h2 className="text-3xl md:text-4xl font-bold">Why use ShopSnap?</h2>
                <p className="text-muted-foreground text-lg">
                  User-generated content is 42% more effective than branded content.
                  Let your customers show off your products in authentic environments.
                </p>

                <div className="space-y-6 pt-4">
                  {benefits.map((benefit, index) => <FadeIn key={index} delay={index * 100} className="flex gap-4">
                    <div className="flex-shrink-0 flex items-start pt-1">
                      <div className="rounded-full bg-primary/10 p-1">
                        <CheckCircle2 className="h-5 w-5 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-medium">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </div>
                  </FadeIn>)}
                </div>

                <div className="pt-6">
                  <Button size="lg" asChild>
                    <Link to="/register">
                      Start Collecting Photos <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </FadeIn>

              <FadeIn direction="left" className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="aspect-square rounded-lg overflow-hidden shadow-lg">
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      >
                        <source src="https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268b952eb30a892c597fd_Hexclad%20Gif2-11-transcode.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <div className="aspect-square rounded-lg overflow-hidden shadow-lg translate-y-8">
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      >
                        <source src="https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268c1f98af825e3ac9d4e_Hexclad%20Gif1-10-transcode.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                  <div className="space-y-4 translate-y-12">
                    <div className="aspect-square rounded-lg overflow-hidden shadow-lg">
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      >
                        <source src="https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268ca1f3d768fd2cbce1a_C4%20Gif2-9-transcode.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                    <div className="aspect-square rounded-lg overflow-hidden shadow-lg">
                      <video
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      >
                        <source src="https://cdn.prod.website-files.com/666bb68c80a39d1a43c135f1%2F667268b3218cf85f1c4770e6_Huppy%20Gif2-12-transcode.mp4" type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>


        <VideoCarousel />

        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 -z-10 opacity-5 bg-[radial-gradient(circle_at_50%_50%,rgba(24,118,255,0.8),rgba(24,118,255,0))]" />

          <div className="container max-w-6xl">
            <FadeIn className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">What Our Customers Say</h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                Hear from brands that have transformed their marketing with authentic customer photos
              </p>
            </FadeIn>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  quote: "We've seen a 40% increase in conversion rates since implementing customer photos on our product pages.",
                  author: "Sarah Johnson",
                  company: "Skin & More Brands"
                },
                {
                  quote: "ShopSnap made collecting and managing customer content so simple. It's now our most valuable marketing asset.",
                  author: "Michael Chen",
                  company: "Fit Fitness Brands"
                },
                {
                  quote: "Our customers love showing off their purchases, and we love the authentic content we get to use in our marketing.",
                  author: "Emily Rodriguez",
                  company: "Living Brands"
                }
              ].map((testimonial, index) => (
                <FadeIn key={index} delay={index * 100} className="bg-card p-8 rounded-xl border border-border shadow-sm">
                  <div className="space-y-4">
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-primary text-primary" />
                      ))}
                    </div>
                    <p className="text-card-foreground italic">"{testimonial.quote}"</p>
                    <div>
                      <p className="font-medium">{testimonial.author}</p>
                      <p className="text-muted-foreground text-sm">{testimonial.company}</p>
                    </div>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 relative overflow-hidden">
          <div className="absolute inset-0 -z-10" style={{
            background: "radial-gradient(50% 100% at 50% 100%, rgba(24, 118, 255, 0.1) 0%, rgba(56, 189, 248, 0) 100%)"
          }} />

          <div className="container max-w-4xl">
            <div className="text-center space-y-6">
              <FadeIn>
                <h2 className="text-3xl md:text-4xl font-bold">Ready to start collecting customer photos?</h2>
                <p className="text-xl text-muted-foreground mt-4 mb-8 max-w-2xl mx-auto">
                  Join hundreds of brands already using ShopSnap to collect and showcase authentic user content.
                </p>
              </FadeIn>

              <FadeIn delay={200}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" asChild>
                    <Link to="/register">
                      Get Started <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/contact">Contact Sales</Link>
                  </Button>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
